/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, { useMemo, FunctionComponent } from "react";
import {
    findNode,
    FolderBrowserView,
    GetInteractivity,
    isAllNodesSelected,
    TreeBrowserDomainNodeContext,
    useTreeBrowser,
    VirtualScrollingTreeBrowserComponent,
    createBreadcrumb,
} from "@iventis/tree-browser";
import { Node } from "@iventis/domain-model/model/node";
import { StyledTreeActionsContainer, TreeBrowserHeaderComponent } from "@iventis/tree-browser/components/tree-browser-header";
import { LimitChipRemaining, LoadingComponent, StyledCustomDialog } from "@iventis/components";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { spatialHeaderIconSize, iconButtonSize, inputHeight, styled } from "@iventis/styles";
import { useWindowSize, canEdit, canRead } from "@iventis/utilities";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import { LimitedProjectResource } from "@iventis/domain-model/model/limitedProjectResource";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { BreadCrumbComponent } from "@iventis/components/src/bread-crumb";
import { NoAccessOverlay } from "@iventis/permissions/src/upgrade-limit-overlay";
import { getLibrarySorts, getLibrarySortByTreeSortId, getTreeSortsFromLibrarySorts, getTreeSortByFieldSort } from "./library-sorting";
import { LibraryNodeExtraProps, libraryNodeComponents } from "./library-nodes";
import { StyledLibraryHeader, StyledTreeBrowserContents } from "./library";

export const EmbeddedLibraryComponent: React.FC<{
    canSelectNode: (node: Node) => { disabled: boolean; tooltip?: string };
    userId?: string;
    libraries: Node[];
    onLibrarySelectionChange?: (library: Node) => void;
    canSwitchLibrary: boolean;
    nodesBeingEdited?: string[];
}> = ({ canSelectNode, userId, libraries, onLibrarySelectionChange, canSwitchLibrary, nodesBeingEdited }) => {
    const librarySorts = useMemo(() => getLibrarySorts, []);

    const [, { functionality, projectUsage }] = useProjectLimiter();

    const translate = useIventisTranslate();

    const [{ expandedNodeIds, selectedNodeIds, sort, mainNodeId, tree, isLoadingTree, singleSelectionOnly }, treeBrowserContext] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        null,
        "expandedNodeIds",
        "selectedNodeIds",
        "sort",
        "mainNodeId",
        "tree",
        "isLoadingTree",
        "singleSelectionOnly"
    );

    const setLibrarySort = (newSortId: string) => {
        const newLibrarySort = getLibrarySortByTreeSortId(newSortId, librarySorts());
        userLocalStorageManager.set(userLocalStorageItems.folderBrowserSort, userId, { field: newLibrarySort.field, action: newLibrarySort.action });
        treeBrowserContext.setSort(newLibrarySort);
    };

    const displayTree = useMemo(() => findNode([tree], mainNodeId), [tree, mainNodeId]);

    const folderPath = useMemo(() => createBreadcrumb(mainNodeId, tree), [mainNodeId, tree]);

    const selectAllToggled = useMemo(() => isAllNodesSelected(displayTree, selectedNodeIds), [selectedNodeIds, displayTree]);

    const treeSorts = useMemo(() => getTreeSortsFromLibrarySorts(librarySorts()), []);

    const hasReadPermissionOnDisplayNode = useMemo(() => canRead(displayTree?.authorisation), [displayTree]);

    const isSharedLibrary = folderPath?.[0]?.type === NodeType.MappingLibrary;

    const getInteractivity: GetInteractivity<Node> = (node) => {
        const selectable = canSelectNode(node);
        return { disabled: false, draggable: false, selectable: { ...selectable }, isNameClickable: node.type === NodeType.Folder };
    };

    const extraProps: LibraryNodeExtraProps = {
        viewType: FolderBrowserView.List,
        embedded: true,
        disableActions: !hasReadPermissionOnDisplayNode,
        nodesBeingEdited,
    };

    return (
        <>
            <StyledLibraryHeader showBorderBottom={false}>
                {folderPath.length === 1 && canSwitchLibrary ? (
                    <Select<Node>
                        onChange={(event: SelectChangeEvent<Node>) => typeof event.target.value !== "string" && onLibrarySelectionChange(event.target.value)}
                        value={folderPath[0]}
                        style={{ alignSelf: "baseline", height: inputHeight }}
                        variant="standard"
                        disableUnderline
                        renderValue={(value: Node) => (
                            <SelectContainer>
                                <div className="header-icon-container">
                                    <FontAwesomeIcon className="header-icon" icon={{ prefix: "fas", iconName: "books" }} />
                                </div>
                                {translate(Content.library2.mappingLibraries[value.name])}
                            </SelectContainer>
                        )}
                        disabled={typeof onLibrarySelectionChange !== "function"}
                        data-cy="select-library"
                    >
                        {libraries.map((library) => (
                            // MUI MenuItems throw a type error despite this being correct behaviour
                            // see: https://github.com/mui/material-ui/issues/14286
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            <MenuItem data-cy={`select-${library.name.replace(" ", "-")}`} key={library.id} id={library.id} value={library as any}>
                                {translate(Content.library2.mappingLibraries[library.name])}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <BreadCrumbComponent<Node>
                        breadcrumbs={folderPath}
                        onElementClick={(node) => {
                            if (singleSelectionOnly) {
                                if (canEdit(node.authorisation) && (!canSelectNode(node).disabled || node.id === tree.id)) {
                                    treeBrowserContext.toggleSelectNodes([node]);
                                } else {
                                    treeBrowserContext.toggleSelectNodes(selectedNodeIds);
                                }
                            }
                            treeBrowserContext.setMainNode(node);
                        }}
                        headerIcon={{ prefix: "fas", iconName: "books" }}
                        className="folder-breadcrumb"
                        droppable={false}
                        translatableContent={Content.library2.mappingLibraries}
                    />
                )}
            </StyledLibraryHeader>
            <TreeBrowserHeaderComponent
                selectAllValue={selectAllToggled}
                selectAll={() => treeBrowserContext.toggleSelectAll()}
                sortMethods={treeSorts}
                selectedSortMethod={getTreeSortByFieldSort(sort, treeSorts)}
                onSortMethodChange={setLibrarySort}
                hideSelectAll
                disabledSort={isLoadingTree}
                disabledSelection={isLoadingTree}
            >
                <StyledTreeActionsContainer>
                    {isSharedLibrary && <LimitChipRemaining currentUsage={projectUsage.teamMaps} maxUsage={functionality.maxTeamMaps} resource={LimitedProjectResource.TeamMap} />}
                </StyledTreeActionsContainer>
            </TreeBrowserHeaderComponent>
            {isLoadingTree ? (
                <LoadingComponent />
            ) : (
                <StyledTreeBrowserContents>
                    <VirtualScrollingTreeBrowserComponent
                        node={displayTree}
                        openNodes={expandedNodeIds}
                        components={libraryNodeComponents}
                        extraProps={extraProps}
                        getInteractivity={getInteractivity}
                    />
                </StyledTreeBrowserContents>
            )}
            {!functionality.sharedWorkspace && isSharedLibrary && <NoAccessOverlay upgradeType="sharedWorkspace" />}
        </>
    );
};

export const EmbeddedLibraryModal: FunctionComponent<{
    close: () => void;
    open: boolean;
    children: React.ReactNode;
    testid?: string;
    style?: { height?: string; minWidth?: string };
}> = ({ children, close, open, testid, style = { height: "80vh" } }) => {
    const { screenWidth } = useWindowSize();
    return (
        <StyledCustomDialog $noPadding $staticHeight={style.height} $minWidth={style?.minWidth} $screenWidth={screenWidth} open={open} onClose={close} data-testid={testid}>
            {children}
        </StyledCustomDialog>
    );
};

export const StyledLibraryContainer = styled.div`
    display: flex;
    flex: 1 1;
    flex-direction: column;
    overflow-y: hidden;
`;

const SelectContainer = styled.div`
    height: ${inputHeight};

    display: flex;
    align-items: center;
    justify-content: flex-start;
    white-space: nowrap;

    .header-icon {
        width: ${spatialHeaderIconSize.width};
        height: ${spatialHeaderIconSize.height};
    }

    .header-icon-container {
        width: ${iconButtonSize};
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
